import { useCallback, useEffect, useRef } from 'react';
import { callBackend } from '../../../utils/api';
import { AUM_UPDATED_EVENT } from '../dashboardUtils';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { RISK_INVALIDATION_CAUSES } from '../orchestration/useRiskOrchestrationCommands';
import { normalizeAumValue } from '../../../utils/aum';

export default function usePortfolioRiskConfig({
    userUid,
    actualPortfolioVersion = 0,
    startupReady = true,
    activeView,
    onStartingValuesLoaded,
    maxPosSizeNormalVolLoaded,
    maxPosSizeNormalVolLoading,
    aumLoaded,
    aumLoading,
    riskCommandsRef,
    setMaxPosSize,
    setNormalVol,
    setAum,
    setMaxPosSizeNormalVolLoading,
    setMaxPosSizeNormalVolLoaded,
    setAumLoading,
    setAumLoaded,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
}) {
    const aumRequestRef = useRef(0);
    const aumScopeRef = useRef(null);
    aumScopeRef.current = { userUid, actualPortfolioVersion };
    const previousPortfolioRef = useRef({ userUid, actualPortfolioVersion });
    // Loads the draft's Max Pos Size and Normal Vol (the member's starting
    // values when there is no draft) and the starting values themselves.
    // There is no client-side default: on failure nothing is set.
    const fetchMaxPosSizeAndNormalVol = useCallback(async () => {
        if (!userUid) return null;
        setMaxPosSizeNormalVolLoading(true);
        try {
            const resp = await callBackend(
                'fetch_max_pos_size_and_normal_vol',
                'POST',
                {},
            );
            if (
                !Number.isFinite(resp?.max_pos_size)
                || !Number.isFinite(resp?.normal_vol)
            ) {
                throw new Error('Position Sizing settings are incomplete.');
            }
            const loaded = onStartingValuesLoaded(resp, userUid);
            setMaxPosSize(resp.max_pos_size);
            setNormalVol(resp.normal_vol);
            return loaded;
        } catch (err) {
            console.error('Error fetching max_pos_size and normal_vol:', err);
            setSnackbarMessage('Failed to fetch max-pos-size and normal-vol');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return null;
        } finally {
            setMaxPosSizeNormalVolLoading(false);
            setMaxPosSizeNormalVolLoaded(true);
        }
    }, [
        onStartingValuesLoaded,
        setMaxPosSize,
        setMaxPosSizeNormalVolLoaded,
        setMaxPosSizeNormalVolLoading,
        setNormalVol,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    const fetchAum = useCallback(async () => {
        if (!userUid) return;
        const requestId = ++aumRequestRef.current;
        const scope = aumScopeRef.current;
        const isCurrent = () => requestId === aumRequestRef.current
            && scope.userUid === aumScopeRef.current.userUid
            && scope.actualPortfolioVersion === aumScopeRef.current.actualPortfolioVersion;
        setAumLoading(true);
        try {
            const resp = await callBackend(
                'fetch_aum',
                'POST',
                {},
            );
            if (isCurrent()) setAum(normalizeAumValue(resp?.aum));
        } catch (err) {
            if (!isCurrent()) return;
            console.error('Error fetching AUM:', err);
            setSnackbarMessage('Failed to fetch AUM.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            if (isCurrent()) {
                setAumLoading(false);
                setAumLoaded(true);
            }
        }
    }, [
        setAum,
        setAumLoaded,
        setAumLoading,
        setSnackbarMessage,
        setSnackbarOpen,
        setSnackbarSeverity,
        userUid,
    ]);

    const refreshActualRiskSurface = useCallback(async () => {
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'usePortfolioRiskConfig.refreshActualRiskSurface',
            ['invalidateRiskSurface', 'refreshRiskSurface'],
        );
        commands.invalidateRiskSurface({
            cause: RISK_INVALIDATION_CAUSES.RISK_CONFIG_CHANGED,
            reason: 'risk_config_change',
        });
        await commands.refreshRiskSurface({
            reason: 'risk_config_change',
            includeOptimizerSummaries: false,
        });
    }, [riskCommandsRef]);

    const refreshAum = useCallback(async () => {
        await fetchAum();
        await refreshActualRiskSurface();
    }, [
        fetchAum,
        refreshActualRiskSurface,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        if (!userUid) return undefined;

        const handleAumUpdated = (event) => {
            const nextAum = normalizeAumValue(event?.detail?.aum);
            if (Number.isFinite(nextAum)) {
                ++aumRequestRef.current;
                setAum(nextAum);
                setAumLoading(false);
                setAumLoaded(true);
                void refreshActualRiskSurface();
                return;
            }
            void refreshAum();
        };

        window.addEventListener(AUM_UPDATED_EVENT, handleAumUpdated);
        return () => {
            window.removeEventListener(AUM_UPDATED_EVENT, handleAumUpdated);
        };
    }, [
        refreshAum,
        refreshActualRiskSurface,
        setAum,
        setAumLoaded,
        setAumLoading,
        userUid,
    ]);

    // The first load can finish before an Actual parent exists. An accepted
    // import changes its version, so reread even if that earlier load was null.
    useEffect(() => {
        const previous = previousPortfolioRef.current;
        previousPortfolioRef.current = { userUid, actualPortfolioVersion };
        if (!userUid || previous.userUid !== userUid
            || previous.actualPortfolioVersion === actualPortfolioVersion) return;
        void fetchAum();
    }, [actualPortfolioVersion, fetchAum, userUid]);

    // Every view loads them: an Actual upload from any page can publish the
    // first Position Sizing draft, which needs the starting values.
    useEffect(() => {
        if (!userUid) return;
        if (!startupReady) return;
        if (!maxPosSizeNormalVolLoaded && !maxPosSizeNormalVolLoading) {
            void fetchMaxPosSizeAndNormalVol();
        }
    }, [
        fetchMaxPosSizeAndNormalVol,
        maxPosSizeNormalVolLoaded,
        maxPosSizeNormalVolLoading,
        startupReady,
        userUid,
    ]);

    useEffect(() => {
        if (!userUid) return;
        const shouldLoadAum = activeView === 'overview'
            || activeView === 'pos-sizing'
            || activeView === 'optimizer';
        if (!shouldLoadAum) return;
        if (!aumLoaded && !aumLoading) {
            void fetchAum();
        }
    }, [activeView, aumLoaded, aumLoading, fetchAum, userUid]);

    return { fetchMaxPosSizeAndNormalVol };
}
